import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Keyboard behaviour for a modal dialog, shared by every dialog in the app.
 *
 *   const dialogRef = useDialog(() => setShowEditor(false));
 *   {showEditor && <div ref={dialogRef} className="fixed inset-0 ...">...</div>}
 *
 * Attach the ref to the dialog's outermost element (the backdrop). While it is mounted:
 *  - focus moves into it: an element marked `data-autofocus`, else the first form field, else the
 *    first focusable control. An `autoFocus` field that already took focus is left alone.
 *  - Tab and Shift+Tab wrap inside it instead of walking out onto the page behind.
 *  - Escape calls `onClose`. If something was typed or picked in the dialog, it first asks whether
 *    to discard it, so a stray Escape cannot throw a half-filled form away.
 *  - when it unmounts, focus goes back to whatever opened it, so the next Tab carries on from there.
 *
 * Before this, none of the dialogs did any of it. Focus stayed on the page behind, so Tab walked
 * through every row of the table under a Register Patient form before reaching its first field, and
 * closing a dialog dropped focus to the top of the page.
 *
 * Options:
 *   escape          false leaves Escape alone (for a dialog that must not be dismissed)
 *   confirmDiscard  false closes on Escape without asking, for dialogs that only show or pick things
 *   isDirty         () => boolean, for a dialog that knows better than the hook whether it holds
 *                   unsaved work: ticks staged in a picker (clicks, which the hook cannot see), or a
 *                   form that saves and stays open (the hook would still remember the typing)
 *   initialFocus    a CSS selector or a ref for the element to focus first
 *
 * Without `isDirty`, typing or picking anywhere in the dialog counts as a change, except in fields
 * marked `data-no-dirty` or inside an element marked with it (search and filter boxes), and in
 * inputs whose placeholder starts with "Search".
 *
 * One document listener serves all dialogs and acts only for the topmost one, so a confirm raised
 * over a form handles its own keys. It listens in the bubble phase and skips events that are already
 * handled: a dropdown open inside a dialog takes its own Escape first (in the capture phase, or by
 * preventing the default) and the dialog stays open.
 */

const TABBABLE = [
  'a[href]',
  'button',
  'input:not([type="hidden"])',
  'select',
  'textarea',
  'iframe',
  'summary',
  '[tabindex]',
  '[contenteditable="true"]',
].join(',');

// What counts as a form field when choosing where focus starts. The trigger buttons of the shared
// Select, SearchableSelect and DatePicker are fields too; checkboxes, radios and file pickers are
// not, since starting on one of those skips past the fields above it.
const FIELD = [
  'input:not([type="checkbox"]):not([type="radio"]):not([type="file"])',
  'select',
  'textarea',
  '[aria-haspopup="listbox"]',
  '[aria-haspopup="dialog"]',
].join(',');

// jsdom does no layout, so nothing in a test would ever count as visible.
const IS_JSDOM = typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '');
const isVisible = (el) => IS_JSDOM || el.getClientRects().length > 0;

export const tabbablesIn = (node) =>
  Array.from(node.querySelectorAll(TABBABLE)).filter(
    (el) => el.tabIndex >= 0 && !el.matches(':disabled') && !el.closest('[inert]') && isVisible(el)
  );

const follows = (a, b) => Boolean(a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING);

const stack = []; // open dialogs, bottom first
const recentFocus = []; // the last few elements to take focus, newest last
let discardConfirm = null;

// UIProvider registers its confirm() here. Kept as a setter rather than a context read so this module
// does not import UIProvider, which itself uses this hook for its confirm and prompt dialogs.
export const setDialogDiscardConfirm = (fn) => {
  discardConfirm = fn;
};

const DISCARD_PROMPT = {
  title: 'Discard changes?',
  message: 'What you have entered in this form will be lost.',
  confirmText: 'Discard',
  cancelText: 'Keep editing',
  tone: 'warning',
};

// The innermost open dialog holding `el`. A dialog can be rendered inside another one's element (a
// new medicine form opened from a stock receipt), and a change made in the inner form is not a change
// to the outer one.
const ownerOf = (el) =>
  stack.reduce(
    (owner, entry) => (entry.node.contains(el) && (!owner || owner.node.contains(entry.node)) ? entry : owner),
    null
  );

// Custom pickers change a form without firing input events, so they report the change themselves.
export const markDialogDirty = (el) => {
  if (!el || el.closest('[data-no-dirty]')) return;
  const owner = ownerOf(el);
  if (owner) owner.dirty = true;
};

// The dialog on top, optionally passing over one (the dialog that is closing).
const topEntry = (skip = null) => {
  for (let i = stack.length - 1; i >= 0; i -= 1) {
    const { node } = stack[i];
    if (stack[i] !== skip && node.isConnected && isVisible(node)) return stack[i];
  }
  return null;
};

// Moves focus to the tab stop after `el` (before it, going backwards), skipping anything inside it,
// and wrapping inside the open dialog if `el` is in one. For a control whose popup sits right after
// it in the page and is being closed by Tab: left to the browser, Tab can land in the popup before
// React has removed it, and focus then falls to the top of the page.
export const focusPastElement = (el, backwards = false) => {
  if (!el) return;
  const top = topEntry();
  const scope = top && top.node.contains(el) ? top.node : document.body;
  const items = tabbablesIn(scope).filter((item) => !el.contains(item));
  if (items.length === 0) return;
  const next = backwards
    ? [...items].reverse().find((item) => follows(item, el))
    : items.find((item) => follows(el, item));
  if (next) next.focus();
  else if (scope !== document.body) (backwards ? items[items.length - 1] : items[0]).focus();
};

const makeFocusable = (node) => {
  if (node.hasAttribute('tabindex')) return;
  node.setAttribute('tabindex', '-1');
  node.style.outline = 'none';
};

const focusInitial = (entry) => {
  const { node } = entry;
  if (node.contains(document.activeElement) && document.activeElement !== node) return;
  const wanted = entry.options().initialFocus;
  let target = null;
  if (typeof wanted === 'string') target = node.querySelector(wanted);
  else if (wanted && wanted.current) target = wanted.current;
  if (!target) target = node.querySelector('[data-autofocus]');
  if (!target) {
    const items = tabbablesIn(node);
    target = items.find((el) => el.matches(FIELD) && !el.readOnly) || items[0] || null;
  }
  if (target) {
    target.focus();
  } else {
    makeFocusable(node);
    node.focus({ preventScroll: true });
  }
};

// The element to hand focus back to later. By the time a dialog's ref attaches, an autoFocus field
// inside it has already taken focus, so the opener is looked up in the recent focus history.
const findOpener = (node) => {
  const active = document.activeElement;
  if (active && active !== document.body && !node.contains(active)) return active;
  for (let i = recentFocus.length - 1; i >= 0; i -= 1) {
    const el = recentFocus[i];
    if (el.isConnected && !node.contains(el)) return el;
  }
  return null;
};

const SEARCH_BOX = /^search/i;
const isTypedField = (el) =>
  el instanceof HTMLElement &&
  (el.matches('input, textarea, select') || el.isContentEditable) &&
  el.type !== 'search' &&
  !SEARCH_BOX.test(el.getAttribute('placeholder') || '') &&
  !el.closest('[data-no-dirty]');

const attach = (node, hook) => {
  const entry = {
    node,
    opener: findOpener(node),
    // Where focus was before, newest last, for when the opener itself is gone by the time the
    // dialog closes (a menu item that closed along with its menu).
    trail: recentFocus.filter((el) => !node.contains(el)),
    dirty: false,
    confirming: false,
    onClose: () => hook.onCloseRef.current,
    options: () => hook.optionsRef.current || {},
  };
  // A dialog that mounts in the same commit as one nested inside it attaches after it (children
  // attach first), so it is slotted in underneath rather than on top.
  const inner = stack.findIndex((other) => node.contains(other.node));
  if (inner >= 0) stack.splice(inner, 0, entry);
  else stack.push(entry);

  if (!node.hasAttribute('role')) node.setAttribute('role', 'dialog');
  if (!node.hasAttribute('aria-modal')) node.setAttribute('aria-modal', 'true');

  entry.onEdit = (e) => {
    if (isTypedField(e.target) && ownerOf(e.target) === entry) entry.dirty = true;
  };
  node.addEventListener('input', entry.onEdit, true);
  node.addEventListener('change', entry.onEdit, true);

  if (topEntry() === entry) focusInitial(entry);
  return entry;
};

// Back to what opened the dialog or, if that has gone, the last element focused before it that is
// still on the page. When another dialog stays open underneath, only somewhere inside that one will
// do: focus must not land on the page behind an open dialog.
const returnFocus = (entry) => {
  const under = topEntry(entry);
  const back = [entry.opener, ...[...entry.trail].reverse()].find(
    (el) =>
      el &&
      el !== document.body &&
      el.isConnected &&
      !entry.node.contains(el) &&
      (!under || under.node.contains(el))
  );
  if (back) back.focus({ preventScroll: true });
  else if (under) focusInitial(under);
};

const detach = (entry) => {
  entry.node.removeEventListener('input', entry.onEdit, true);
  entry.node.removeEventListener('change', entry.onEdit, true);
  // Only when focus is still in the closing dialog (or was dropped). A close handler that moved
  // focus somewhere on purpose keeps it there. Focus moves while the dialog is still on the stack,
  // because a text field fires `change` as it loses focus, and that change belongs to this dialog,
  // not to the one underneath.
  const active = document.activeElement;
  if (!active || active === document.body || entry.node.contains(active)) returnFocus(entry);
  const at = stack.indexOf(entry);
  if (at >= 0) stack.splice(at, 1);
};

const trapTab = (entry, e) => {
  const { node } = entry;
  const items = tabbablesIn(node);
  if (items.length === 0) {
    e.preventDefault();
    makeFocusable(node);
    node.focus({ preventScroll: true });
    return;
  }
  const active = document.activeElement;
  if (!active || !node.contains(active)) {
    e.preventDefault();
    (e.shiftKey ? items[items.length - 1] : items[0]).focus();
    return;
  }
  // Compared by document position rather than by index, so focus sitting on something that is not
  // itself a tab stop (the dialog, or an option a click left focused) still wraps at the ends.
  if (e.shiftKey) {
    if (!items.some((el) => el !== active && follows(el, active))) {
      e.preventDefault();
      items[items.length - 1].focus();
    }
  } else if (!items.some((el) => el !== active && follows(active, el))) {
    e.preventDefault();
    items[0].focus();
  }
};

const escape = (entry, e) => {
  const onClose = entry.onClose();
  const options = entry.options();
  if (!onClose || options.escape === false) return;
  e.preventDefault();
  if (entry.confirming) return;
  const dirty = typeof options.isDirty === 'function' ? options.isDirty() : entry.dirty;
  if (dirty && options.confirmDiscard !== false && discardConfirm) {
    entry.confirming = true;
    Promise.resolve(discardConfirm(DISCARD_PROMPT)).then((ok) => {
      entry.confirming = false;
      const latest = entry.onClose();
      if (ok && latest && stack.includes(entry)) latest();
    });
    return;
  }
  onClose();
};

const onDocumentKeyDown = (e) => {
  if (e.defaultPrevented || (e.key !== 'Tab' && e.key !== 'Escape')) return;
  const entry = topEntry();
  if (!entry) return;
  if (e.key === 'Tab') trapTab(entry, e);
  else escape(entry, e);
};

const onDocumentFocusIn = (e) => {
  const el = e.target;
  if (!(el instanceof HTMLElement)) return;
  const at = recentFocus.indexOf(el);
  if (at >= 0) recentFocus.splice(at, 1);
  recentFocus.push(el);
  if (recentFocus.length > 10) recentFocus.shift();
};

if (typeof document !== 'undefined') {
  document.addEventListener('focusin', onDocumentFocusIn, true);
  document.addEventListener('keydown', onDocumentKeyDown);
}

/**
 * Whether `value` differs from what it was when `key` last changed: an `isDirty` for a dialog whose
 * form saves and starts again without the dialog closing. Pass a key that changes whenever the form
 * starts afresh, so the baseline is what the form opened with, not what the dialog first showed:
 *
 *   const noteChanged = useChangedSince(showForm ? draft : null, `${showForm}:${editingId}`);
 *   const dialogRef = useDialog(onClose, { isDirty: () => noteChanged });
 */
export function useChangedSince(value, key) {
  const json = JSON.stringify(value ?? null);
  const [baseline, setBaseline] = useState({ key, json });
  if (baseline.key !== key) {
    setBaseline({ key, json });
    return false;
  }
  return json !== baseline.json;
}

export default function useDialog(onClose, options) {
  const onCloseRef = useRef(onClose);
  const optionsRef = useRef(options);
  useEffect(() => {
    onCloseRef.current = onClose;
    optionsRef.current = options;
  });
  const entryRef = useRef(null);
  // A callback ref rather than an effect, so it follows the element itself: most dialogs are rendered
  // conditionally inside a component that stays mounted, and an effect would not see them come and go.
  return useCallback((node) => {
    if (entryRef.current) {
      detach(entryRef.current);
      entryRef.current = null;
    }
    if (node) entryRef.current = attach(node, { onCloseRef, optionsRef });
  }, []);
}
